import { XMarkIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Combobox } from "components/elements";
import { useAllOrganizations } from "hooks/useAllOrganizations";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";

export interface PermissionEntry {
    target: "USER" | "ORGANIZATION" | "COMPASS_TEAM" | "PUBLIC";
    targetId: number;
    label: string;
    role: "OWNER" | "EDITOR" | "VIEWER" | "DENIED";
    orgLabel?: string;
    orgId?: number;
}

interface ChartPermissionPickerProps {
    value: PermissionEntry[];
    onChange: (value: PermissionEntry[]) => void;
    onBeforeAdd?: (entry: PermissionEntry) => Promise<boolean>;
}

const ROLES = ["VIEWER", "EDITOR", "OWNER", "DENIED"] as const;
const PAGE_SIZE = 30;

// We use a negative ID for the "entire org" option so it doesn't clash with user IDs
const ENTIRE_ORG_SENTINEL = -1;
// Sentinel for the "type to search" hint — won't be selectable
const MORE_RESULTS_SENTINEL = -2;

export const ChartPermissionPicker = ({
    value,
    onChange,
    onBeforeAdd,
}: ChartPermissionPickerProps) => {
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const lastQueryRef = useRef("");
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Reset search when org changes
    useEffect(() => {
        lastQueryRef.current = "";
        setDebouncedSearch("");
    }, [selectedOrgId]);

    // Org list source depends on who is sharing:
    //  - Compass users get every org via the cached, app-wide `useAllOrganizations`
    //    query (replaces the 20-row cap in `permissionTargets` — PRTL-1272).
    //  - Non-Compass users only see their own organization, so they can still
    //    pick teammates from it. With no source of "all orgs" they would
    //    otherwise see an empty dropdown.
    const currentUser = useCurrentUser();
    const { data: allOrganizations } = useAllOrganizations(currentUser ?? undefined);
    const hasCompassTeam = Boolean(currentUser?.isCompassUser);

    const { data: usersData, isFetching: isLoadingUsers } = trpc.users.list.useQuery(
        {
            organizationId: selectedOrgId!,
            pageSize: PAGE_SIZE,
            search: debouncedSearch || undefined,
        },
        {
            enabled: selectedOrgId !== null,
            placeholderData: (prev) => prev,
        },
    );

    const addedKeys = new Set(value.map((p) => `${p.target}:${p.targetId}`));

    const orgOptions = useMemo(() => {
        if (currentUser?.isCompassUser) {
            return (allOrganizations ?? []).map((o) => ({ id: o.id, label: o.name }));
        }
        if (currentUser?.organizationId && currentUser.organizationName) {
            return [{ id: currentUser.organizationId, label: currentUser.organizationName }];
        }
        return [];
    }, [
        currentUser?.isCompassUser,
        currentUser?.organizationId,
        currentUser?.organizationName,
        allOrganizations,
    ]);

    const selectedOrg = orgOptions.find((o) => o.id === selectedOrgId);
    const compassTeamAdded = addedKeys.has("COMPASS_TEAM:0");

    const getOrgComboboxOptions = useCallback(
        (query: string) => {
            const orgs = orgOptions;
            if (!query) return orgs.map((o) => ({ value: o.id, display: o.label }));
            const q = query.toLowerCase();
            return orgs
                .filter((o) => o.label.toLowerCase().includes(q))
                .map((o) => ({ value: o.id, display: o.label }));
        },
        [orgOptions],
    );

    const getUserComboboxOptions = useCallback(
        (query: string) => {
            // Schedule a debounced search update when the query changes
            if (query !== lastQueryRef.current) {
                lastQueryRef.current = query;
                if (debounceRef.current) clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => setDebouncedSearch(query), 250);
            }

            // Show loading while waiting for initial data
            if (selectedOrgId !== null && !usersData && isLoadingUsers) return null;

            const results: Array<{ value: number; display: string }> = [];

            // First option: "Entire [Org] org" (if not already added)
            if (selectedOrg && !addedKeys.has(`ORGANIZATION:${selectedOrg.id}`)) {
                const entireLabel = `Entire ${selectedOrg.label} org`;
                if (!query || entireLabel.toLowerCase().includes(query.toLowerCase())) {
                    results.push({ value: ENTIRE_ORG_SENTINEL, display: entireLabel });
                }
            }

            // Individual users from server
            const users = usersData?.users ?? [];
            for (const u of users) {
                if (addedKeys.has(`USER:${u.id}`)) continue;
                const display = `${u.name} (${u.email})`;
                results.push({ value: u.id, display });
            }

            // Show hint when there are more results than what we fetched
            const total = usersData?.totalFound ?? 0;
            if (total > PAGE_SIZE && !query) {
                const remaining = total - PAGE_SIZE;
                results.push({
                    value: MORE_RESULTS_SENTINEL,
                    display: `${remaining} more user${remaining === 1 ? "" : "s"} — type to search`,
                });
            }

            return results;
        },
        [usersData, selectedOrg, addedKeys.size, selectedOrgId, isLoadingUsers],
    );

    const handleOrgSelect = (id: number | null | undefined) => {
        setSelectedOrgId(id ?? null);
    };

    const handleUserSelect = async (id: number | null | undefined) => {
        if (id == null || !selectedOrg) return;
        // Ignore the "more results" hint
        if (id === MORE_RESULTS_SENTINEL) return;

        let newEntry: PermissionEntry;
        if (id === ENTIRE_ORG_SENTINEL) {
            newEntry = {
                target: "ORGANIZATION",
                targetId: selectedOrg.id,
                label: selectedOrg.label,
                role: "VIEWER",
                orgId: selectedOrg.id,
            };
        } else {
            const user = usersData?.users.find((u) => u.id === id);
            if (!user) return;
            newEntry = {
                target: "USER",
                targetId: id,
                label: user.name,
                role: "VIEWER",
                orgLabel: selectedOrg.label,
                orgId: selectedOrg.id,
            };
        }

        if (onBeforeAdd) {
            const confirmed = await onBeforeAdd(newEntry);
            if (!confirmed) return;
        }

        onChange([...value, newEntry]);
    };

    const handleAddCompassTeam = () => {
        onChange([
            ...value,
            { target: "COMPASS_TEAM", targetId: 0, label: "Compass Team", role: "VIEWER" },
        ]);
    };

    const handleRoleChange = (index: number, role: "OWNER" | "EDITOR" | "VIEWER" | "DENIED") => {
        const next = [...value];
        next[index] = { ...next[index], role };
        onChange(next);
    };

    const handleRemove = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-4">
            {/* Add controls */}
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                        Organization
                    </label>
                    <Combobox
                        value={selectedOrgId}
                        onChange={handleOrgSelect}
                        displayValue={(id) => orgOptions.find((o) => o.id === id)?.label ?? ""}
                        options={getOrgComboboxOptions}
                        placeholder="Select an organization..."
                        withClear
                    />
                </div>
                <div className="flex-1">
                    <label className="mb-1 block text-sm font-medium text-gray-700">User</label>
                    <div className={clsx(!selectedOrgId && "pointer-events-none opacity-50")}>
                        <Combobox
                            value={null}
                            onChange={handleUserSelect}
                            displayValue={() => ""}
                            options={getUserComboboxOptions}
                            placeholder={
                                selectedOrgId
                                    ? "Search users or add entire org..."
                                    : "Select an organization first"
                            }
                        />
                    </div>
                </div>
            </div>
            {hasCompassTeam && !compassTeamAdded && (
                <div>
                    <button
                        type="button"
                        onClick={handleAddCompassTeam}
                        className="whitespace-nowrap rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                        + Compass Team
                    </button>
                </div>
            )}

            {/* Permissions table */}
            {value.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                <th className="px-4 py-2">Name</th>
                                {ROLES.map((r) => (
                                    <th key={r} className="px-3 py-2 text-center">
                                        {r.charAt(0) + r.slice(1).toLowerCase()}
                                    </th>
                                ))}
                                <th className="px-2 py-2" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {value.map((entry, i) => (
                                <tr key={`${entry.target}:${entry.targetId}`} className="text-sm">
                                    <td className="px-4 py-2.5">
                                        <div className="font-medium text-gray-900">
                                            {entry.label}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {entry.target === "PUBLIC"
                                                ? "Public access"
                                                : entry.target === "COMPASS_TEAM"
                                                  ? "Team"
                                                  : entry.target === "ORGANIZATION"
                                                    ? "Organization"
                                                    : entry.orgLabel
                                                      ? `User, ${entry.orgLabel}`
                                                      : "User"}
                                        </div>
                                    </td>
                                    {ROLES.map((r) => {
                                        const isDenied = r === "DENIED";
                                        const disabledForTarget =
                                            isDenied && entry.target !== "USER";
                                        return (
                                            <td key={r} className="px-3 py-2.5 text-center">
                                                {disabledForTarget ? (
                                                    <div className="inline-flex h-5 w-5 items-center justify-center rounded-full border-2 border-gray-200 opacity-30" />
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRoleChange(i, r)}
                                                        className={clsx(
                                                            "inline-flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors",
                                                            entry.role === r && isDenied
                                                                ? "border-red-600 bg-red-600"
                                                                : entry.role === r
                                                                  ? "border-blue-600 bg-blue-600"
                                                                  : isDenied
                                                                    ? "border-red-300 hover:border-red-400"
                                                                    : "border-gray-300 hover:border-gray-400",
                                                        )}
                                                    >
                                                        {entry.role === r && (
                                                            <div className="h-2 w-2 rounded-full bg-white" />
                                                        )}
                                                    </button>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className="px-2 py-2.5 text-center">
                                        <button
                                            type="button"
                                            onClick={() => handleRemove(i)}
                                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
                                        >
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-sm text-gray-500">
                    No permissions added. Only you will have access.
                </p>
            )}
        </div>
    );
};
